import React from 'react';
import ThemeProvider from 'threads5/themes/theme-provider';

import {
  Section,
  Row,
  Column,
  Stack,
  Container,
} from 'src/components-v2/Layout';
import RichTextSwitch from 'src/containers/RichTextSwitch/RichTextSwitch';
import { useCmsMode } from 'src/contexts/ModeContext/ModeContext';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import { CmsThemeContextProvider } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';
import { makeImageQueryString, makeImageSource } from 'src/lib/utils';
import CmsMediaSwitch from 'src/components-v2/Media/CmsMediaSwitch/CmsMediaSwitch';
import {
  StyledBackgroundBox,
  StyledContainer,
  StyledContentStack,
  StyledImageStack,
  StyledMediaSwitch,
} from './CmsCtaAdornmentBanner.styled';

const backgroundQueryString = {
  xs: '?fm=webp&q=60&w=585',
  sm: '?fm=webp&q=60&w=864',
  md: '?fm=webp&q=60&w=1000',
  lg: '?fm=webp&q=60&w=1140',
  xl: '?fm=webp&q=60&w=1375',
};

const backgroundFillStyles = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const getRenderableBackgroundAssetEntry = (backgroundAsset) => {
  const isRenderableImage =
    backgroundAsset?.fields?.appearance === 'Image' &&
    backgroundAsset?.fields?.largeImage?.fields?.file?.url;
  return isRenderableImage ? backgroundAsset : undefined;
};

export const CmsCtaAdornmentBanner = ({ fields, sys }) => {
  const bgColor = fields?.backgroundColor?.toLowerCase() || 'transparent';
  const mode = useCmsMode({
    mode: fields?.mode,
    backgroundColor: bgColor,
  });
  const componentTheme = useComponentTheme(mode);
  const backgroundAssetEntry = getRenderableBackgroundAssetEntry(
    fields?.backgroundAsset,
  );
  const backgroundImage = fields?.backgroundImage?.fields?.file?.url;
  const background =
    !backgroundAssetEntry && backgroundImage
      ? `url(${backgroundImage}) no-repeat center center`
      : componentTheme.palette[fields?.backgroundColor];
  const { lgImgSrc, mdImgSrc, smImgSrc } = makeImageSource(
    fields?.asset?.fields,
  );

  return (
    <CmsThemeContextProvider cmsTheme='web 3.0'>
      <ThemeProvider theme={componentTheme}>
        <Section sx={{ ...fields?.styles }}>
          <Container>
            <Row>
              <Column xsOffset={1} xs={22} mdOffset={2} md={20}>
                <StyledContainer className='cms-cta-adornment-banner-box'>
                  <Stack
                    className='cms-cta-adornment-banner-stack'
                    direction={{
                      xs: fields?.isFlipped ? 'column-reverse' : 'column',
                      sm: fields?.isFlipped ? 'row-reverse' : 'row',
                    }}
                    alignItems={{ xs: 'center', sm: 'stretch' }}
                    sx={{
                      background,
                      backgroundSize: 'cover',
                    }}
                  >
                    {backgroundAssetEntry && (
                      <StyledBackgroundBox className='cms-cta-adornment-banner-background'>
                        <CmsMediaSwitch
                          asset={{
                            ...backgroundAssetEntry.fields,
                            description: '',
                            isLazy: true,
                            width: undefined,
                            height: undefined,
                            styles: {
                              ...backgroundAssetEntry.fields.styles,
                              ...backgroundFillStyles,
                            },
                          }}
                          role='presentation'
                          queryString={backgroundQueryString}
                          sys={backgroundAssetEntry.sys}
                        />
                      </StyledBackgroundBox>
                    )}
                    <StyledContentStack
                      className='cms-cta-adornment-banner-stack-item'
                      direction='column'
                      gap={2}
                      alignItems='start'
                    >
                      <RichTextSwitch
                        sys={sys}
                        content={fields?.adornmentBody}
                        nodes={{
                          h2: {
                            variant: 'h2',
                            component: 'h2',
                            fieldId: 'adornmentBody',
                          },
                          h3: {
                            variant: 'h3',
                            component: 'h2',
                            fieldId: 'adornmentBody',
                          },
                          h4: {
                            variant: 'h4',
                            component: 'h2',
                            fieldId: 'adornmentBody',
                          },
                          h6: {
                            variant: 'h6',
                            component: 'span',
                            fieldId: 'adornmentBody',
                          },
                          p: {
                            variant: 'p',
                            component: 'p',
                            fieldId: 'adornmentBody',
                          },
                        }}
                      />
                    </StyledContentStack>
                    {fields?.asset && (
                      <StyledImageStack
                        className='cms-cta-adornment-banner-stack-item-image'
                        direction='column'
                        justifyContent='center'
                        alignItems='center'
                      >
                        <StyledMediaSwitch
                          asset={fields?.asset?.fields}
                          queryString={{ xl: '?fm=webp&w=860&q=60' }}
                          sources={{
                            xs: makeImageQueryString({
                              src: smImgSrc,
                              width: 585,
                            }),
                            sm: makeImageQueryString({
                              src: smImgSrc,
                              width: 320,
                            }),
                            md: makeImageQueryString({
                              src: mdImgSrc,
                              width: 430,
                            }),
                            lg: makeImageQueryString({
                              src: lgImgSrc,
                              width: 430,
                            }),
                            xl: makeImageQueryString({
                              src: lgImgSrc,
                              width: 430,
                            }),
                          }}
                          sys={fields?.asset?.sys}
                          width={430}
                          height={348}
                        />
                      </StyledImageStack>
                    )}
                  </Stack>
                </StyledContainer>
              </Column>
            </Row>
          </Container>
        </Section>
      </ThemeProvider>
    </CmsThemeContextProvider>
  );
};
